import type { Point } from "./types";
import { LEAGUE } from "./levels";

/**
 * Length of an *open* path along `order` (no closing leg back to the start) —
 * the live distance readout while a route is still being charted. The shared
 * `tourLength` in @jpjj/solver-core always closes the loop, so it is used only
 * once a round is complete.
 */
export function openLength(order: number[], dist: number[][]): number {
  let total = 0;
  for (let i = 0; i + 1 < order.length; i++) total += dist[order[i]][order[i + 1]];
  return total;
}

/** Format map units as leagues for display (one decimal, e.g. "184.1"). */
export function leagues(units: number): string {
  return (units / LEAGUE).toFixed(1);
}

/* ---------- Crossing detection ----------
 * In the Euclidean plane an optimal tour never crosses itself (uncrossing two
 * legs always shortens the round) — so a crossing is an honest "this can be
 * shorter" hint the game can show the player.
 */

/** Orientation of the triple (a,b,c): >0 left turn, <0 right turn, 0 collinear. */
function orient(a: Point, b: Point, c: Point): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

/** True if c lies on the closed segment ab (callers ensure collinearity). */
function onSegment(a: Point, b: Point, c: Point): boolean {
  return (
    Math.min(a[0], b[0]) <= c[0] && c[0] <= Math.max(a[0], b[0]) &&
    Math.min(a[1], b[1]) <= c[1] && c[1] <= Math.max(a[1], b[1])
  );
}

/** True if segments pq and rs intersect (touching counts as crossing). */
export function segmentsCross(p: Point, q: Point, r: Point, s: Point): boolean {
  const d1 = orient(r, s, p);
  const d2 = orient(r, s, q);
  const d3 = orient(p, q, r);
  const d4 = orient(p, q, s);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  if (d1 === 0 && onSegment(r, s, p)) return true;
  if (d2 === 0 && onSegment(r, s, q)) return true;
  if (d3 === 0 && onSegment(p, q, r)) return true;
  if (d4 === 0 && onSegment(p, q, s)) return true;
  return false;
}

/**
 * Indices of legs that cross another leg in the closed tour `order`
 * (leg i runs order[i] → order[(i+1) % n]). Legs sharing an endpoint are
 * adjacent on the tour and skipped. Empty set = an uncrossed round.
 */
export function crossingLegs(order: number[], points: Point[]): Set<number> {
  const n = order.length;
  const crossed = new Set<number>();
  if (n < 4) return crossed;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      // skip the same leg and the two neighbours that share an endpoint
      if (j === i || j === (i + 1) % n || (j + 1) % n === i) continue;
      const a = points[order[i]], b = points[order[(i + 1) % n]];
      const c = points[order[j]], d = points[order[(j + 1) % n]];
      if (segmentsCross(a, b, c, d)) {
        crossed.add(i);
        crossed.add(j);
      }
    }
  }
  return crossed;
}
